import { ChallengeType, CreateGatedAccessEventRequest, CreatePaywallRoleAssignmentRequest, CreatorProfileCardType, CreatorProfileImageSize, EmailGateDetails, GateType, GetCreatorProfileResponse, GetPointResponse, GetPublicOrganizationProductResponse, LoginOrigin, MapDrawer, ObjectId, OrganizationProductStatus, PasswordGateDetails, PaymentTargetType, TopicGateType } from '../types';
import { LitElement, css, html } from 'lit';
import { addBookmark, login, upgradeToken } from '../helpers/api/account-api';
import { customElement, property, query, state } from 'lit/decorators';
import { debounce, delay, getIpAddress, isDefined, show_confetti, undefinedIfEmpty } from '../helpers/utils';

import { FormCheckbox } from './theme/form-checkbox';
import { FormInputButton } from './theme/form-input-button';
import { MapComponent } from './map-component';
import { PreCheckoutComponent } from './pre-checkout-component';
import { StateController } from '../helpers/state/state-management/state-controller';
import { TextField } from '@material/mwc-textfield';
import accountInfo from '../helpers/state/account-info';
import { addEmailCapture } from '../helpers/api/email-capture-api';
import { addGatedAccessEvent } from '../helpers/api/gated-access-api';
import challengeData from '../helpers/state/challenge-data';
import { detectMobileViewport } from '../helpers/device';
import featuresInfo from '../helpers/state/features-info';
import format from 'date-fns/format';
import { getCreatorProfile } from '../helpers/api/creator-profile-api';
import { getMainPage } from '../helpers/page-name';
import { getOrganizationProductsPublic } from '../helpers/api/products-api';
import { getTopic } from '../helpers/api/topics-api';
// import { onBuildMapClick } from '../helpers/build-new-topic';
import pointCache from '../helpers/state/point-cache';
import { startStripeCheckoutSession } from '../helpers/api/stripe-api';
import topicData from '../helpers/state/topic-data';
import { validateEmail } from '../helpers/strings';

@customElement('creator-info-component')
export class CreatorInfoComponent extends LitElement {
    static amplitude_callsite = 'Map Info Drawer';

    @property({ type: String })
    creator_profile_id?: string;

    @property({type: Boolean})
    isUserPreview = false;

    @query('#passwordInput')
    passwordInputElement!: FormInputButton;

    @query('#preCheckout')
    private readonly preCheckoutElement?: PreCheckoutComponent | null;

    @query('#search')
    search!: TextField;

    @query('#emailInput')
    emailInputElement!: FormInputButton;

    @query('#emailInputBookmark')
    emailInputBookmarkElement!: FormInputButton;

    @query('#codeInputBookmark')
    codeInputBookmarkElement!: FormInputButton;

    @query('#emailGateOptIn')
    emailOptInElement!: FormCheckbox;

    @query('#codeInput')
    codeInputElement!: FormInputButton;

    @property()
    mapComponent!: MapComponent;

    @state()
    profile?: GetCreatorProfileResponse;

    @state()
    profile_url?: string;

    @state()
    gatePrice?: number;

    @state()
    showGetEmail: boolean = false;

    @state()
    invalidEmail: boolean = false;

    @state()
    showConfirmEmail: boolean = false;

    @state()
    email: string = accountInfo.email ?? '';

    @state()
    paywallGate?: GetPublicOrganizationProductResponse;

    @state()
    emailGate?: EmailGateDetails;

    @state()
    hasPasswordGate: boolean = false;

    @state()
    hasEmailGate: boolean = false;

    @state()
    isInvalidPassword: boolean = false;

    @state()
    isReloading: boolean = false;

    @state()
    errorOnSubmit: boolean = false;

    @state()
    showConfirmEmailBookmark: boolean = false;

    @state()
    mobilePreview: boolean = false;

    @state()
    hasUserGainedAccess: boolean = false;

    @state()
    userMaybeAlreadyPaid: boolean = false;

    @state()
    showAboutMap: boolean = true;

    @state()
    closeGatedAccess: boolean = false;

    @state()
    isAdminPortal: boolean = false;

    @state()
    isMobile: boolean = detectMobileViewport(this.mobilePreview);

    @state()
    height: string = '100%';

    @state()
    checkoutUrl: string = '';

    @state()
    searchEverywhere: boolean = false;

    @state()
    pointsOutOfView: number = 0;

    @state()
    processingGate: undefined | 'paywall' | 'email' | 'password';

    @property({ type: Boolean })
    bypassGoogleKey: boolean = false;

    @state()
    isCheckingOut: boolean = false;

    @state()
    paywallProducts: GetPublicOrganizationProductResponse[] = [];

    @state()
    invalidCode: boolean = false;

    private readonly bindTopicState = new StateController(this, topicData);
    private readonly bindPointCacheState = new StateController(this, pointCache);

    constructor() {
        super();
        this.updatePointsOutOfView();
    }

    async firstUpdated() {
        await this.startup();
        document.documentElement.style.setProperty('--custom-font', topicData.topic?.custom_font ?? 'Montserrat');
        document.documentElement.style.setProperty('--main-font', topicData.topic?.custom_font ?? 'Montserrat');
        this.style.setProperty('font-family', topicData.topic?.custom_font ?? 'Montserrat');
        this.style.setProperty('--mdc-typography-button-font-family', topicData.topic?.custom_font ?? 'Montserrat');
        // add listener for bounds changed to update points out of view button
        pointCache.map?.addListener(
            'bounds_changed',
            debounce(() => {
                const handler = async () => {
                    this.onPointsChange();
                };
                handler().catch((e) => {
                    return 'Error in getting out of bounds points';
                });
            }, 1000, false)
        );

        if (this.isMobile) {
            this.showAboutMap = false;
        }

        // look for checkout_close query parameter
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.has('checkout_close')) {
            const checkoutSuccess = urlParams.get('checkout_close');
            if (checkoutSuccess === 'true') {
                this.isCheckingOut = true;
                this.processingGate = 'paywall';
                await delay(5000);
                await this.refreshAccess(this.email, GateType.Paywall, this.paywallGate?._id.$oid);
                // remove the query parameter from the URL
                urlParams.delete('checkout_close');
                const newUrl = urlParams.toString() ? `${window.location.pathname}?${urlParams.toString()}` : window.location.pathname;
                window.history.replaceState({}, '', newUrl);
            }
        }
    }

    updatePointsOutOfView() {
        this.pointsOutOfView = pointCache.points.length - pointCache.markersInBounds.length;
    }

    onPointsChange() {
        this.updatePointsOutOfView();
        this.requestUpdate();
    }

    protected async updated(changedProperties: Map<string | number | symbol, unknown>): Promise<void> {
        if (changedProperties.has('creator_profile_id')) {
            await this.startup();
        }

        if (changedProperties.has('isUserPreview')) {
            if (this.isUserPreview) {
                if (this.mobilePreview) {
                    // set all margins that are 1.5rem to 0
                    const element1 = this.shadowRoot?.querySelector('.creator-info');
                    if (element1) {
                        element1?.setAttribute('style', 'padding: 0');
                    }
                    const element2 = this.shadowRoot?.querySelector('.drawer-buttons-group');
                    if (element2) {
                        element2?.setAttribute('style', 'padding: 0');
                    }
                    const element3 = this.shadowRoot?.querySelector('.mid-container');
                    if (element3) {

                        // remove scrolling
                        element3?.setAttribute('style', 'overflow: hidden');
                        element3?.setAttribute('style', 'padding: 0rem 0rem 0rem 0rem');
                    }
                }
            }
            await this.startup();
        }
    }

    private async startup() {

        if (isDefined(this.creator_profile_id)) {
            this.profile = await getCreatorProfile(MapComponent.amplitude_callsite, this.creator_profile_id);
            this.profile_url = this.profile?.link_set.public_link ?? `#creator-profile?profile_id=${this.creator_profile_id ?? ''}`;
        } else {
            this.profile = undefined;
            this.profile_url = undefined;
        }

        if (window.location.href.includes('admin-portal') && topicData.isWriter && !this.isUserPreview) {
            this.isAdminPortal = true;
        }

        this.isMobile = detectMobileViewport(this.mobilePreview);

        let gate_id: string | undefined;

        // First check the hash fragment (for URLs like #gate_id=abc)
        const fragment = window.location.hash.substring(1); // remove the '#' character
        if (fragment) {
            const fragmentParams = new URLSearchParams(fragment);
            gate_id = fragmentParams.get('gate_id') ?? undefined;
        }

        // If not found in fragment, check URL search parameters (for URLs like ?gate_id=abc)
        if (!gate_id) {
            const searchParams = new URLSearchParams(window.location.search);
            gate_id = searchParams.get('gate_id') ?? undefined;
        }

        // see if there are any active password gates
        this.hasPasswordGate = isDefined(await topicData.activeGateDetails(GateType.Password) as PasswordGateDetails);
        this.emailGate = await topicData.activeGateDetails(GateType.Email) as EmailGateDetails;

        this.hasEmailGate = isDefined(this.emailGate);

        await this.getPaywallProducts();
        this.paywallGate = isDefined(gate_id) ? this.paywallProducts.find((product) =>
            'Paywall' in product.item_details && product.item_details.Paywall.gate_id?.$oid === gate_id) : this.paywallProducts.filter((product) => 'Paywall' in product.item_details && product.status === OrganizationProductStatus.Active)[0];
    }

    async getPaywallProducts() {
        try {
            this.paywallProducts = await getOrganizationProductsPublic(PaymentTargetType.Paywall, topicData.topicId, topicData.topic?.organization_id?.$oid ?? '');
        } catch (error) {
            this.paywallProducts = [];
            getMainPage().errorToastElement.showToastAndThrow('Unable to load paywall products. Please contact support.');
        }
    }


    private onClose() {
        this.dispatchEvent(new CustomEvent('on-close'));
    }

    private async buildMap() {
        const link = document.createElement('a');
        link.href = `https://www.proxi.co/?utm_source=embed&utm_medium=referral&utm_content=build_your_own&utm_term=${topicData.topicId}`;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.click();
    }

    private showEmail() {
        this.showGetEmail = true;
        this.requestUpdate();
    }

    async sendPassword() {
        const givenPass = this.passwordInputElement.value;
        let email : string | undefined;
        if (accountInfo.isAuthenticated && accountInfo.email !== '') {
            email = accountInfo.email;
        } else {
            email = undefined;
        }
        await this.refreshAccess(email, GateType.Password, undefined, givenPass);
    }

    private aboutMapClick() {
        this.showAboutMap = !this.showAboutMap;
    }

    get hasGate() {
        return isDefined(this.paywallGate) || this.hasPasswordGate || this.hasEmailGate;
    }

    async submitEmailForBookmark(email: string) {
        const validEmail = validateEmail(email);
        if (!validEmail) {
            getMainPage().errorToastElement.showToastAndThrow('Please enter a valid email address');
        }

        await login(CreatorInfoComponent.amplitude_callsite, email, LoginOrigin.PasswordPaywall, topicData.topic?.name, topicData.topic?.header_logo);
        this.showConfirmEmailBookmark = true;
        this.email = email.toLowerCase();
    }

    async submitEmailForPurchaseVerification(email: string) {
        await login(CreatorInfoComponent.amplitude_callsite, email.toLowerCase(), LoginOrigin.PasswordPaywall, topicData.topic?.name, topicData.topic?.header_logo);
        this.showConfirmEmail = true;
        this.email = email.toLowerCase();
    }

    async submitEmailForEmailGate(email: string) {
        const validEmail = validateEmail(email);
        if (!validEmail) {
                getMainPage().errorToastElement.showToastAndThrow('Please enter a valid email address');
            return;
        }

        if (this.emailGate?.require_opt_in ===true && isDefined(undefinedIfEmpty(this.emailGate.opt_in_language))) {

            if (this.emailOptInElement.checked === false) {
                getMainPage().errorToastElement.showToastAndThrow('You must opt in to proceed.');
                return;
            }
        }
        this.email = email;
        if (this.emailGate?.require_verification === true) {
            await login(CreatorInfoComponent.amplitude_callsite, email, LoginOrigin.PasswordPaywall, topicData.topic?.name, topicData.topic?.header_logo);
            this.showConfirmEmail = true;
        } else {
            await this.refreshAccess(email, GateType.Email);
        }
    }

    async submitEmailForPaywall(email: string) {
        await login('Chargebee Paywall Payment', email, LoginOrigin.PasswordPaywall, topicData.topic?.name, topicData.topic?.header_logo);
        this.showConfirmEmail = true;
    }

    private async alreadyPaidClick() {
        this.processingGate = 'paywall';
        this.userMaybeAlreadyPaid = true;
        this.showGetEmail = true;
    }

    private async closeGatedAccessClick() {
        this.closeGatedAccess = true;
    }

    private async refreshAccess(email: string | undefined, type: GateType, gate_id?: string, password?: string) {
        // if type is password, ensure that password exists, else return
        if (type === GateType.Password && !isDefined(password)) {
            return;
        } else if (type === GateType.Paywall && !isDefined(gate_id)) {
            return;
        } else if (type === GateType.Email && !isDefined(email)) {
            return;
        }
        const accessCode = type === GateType.Password ? password : type === GateType.Email ? email : undefined;
        // pre - test to see if password is going to work
        if ((type === GateType.Password || type === GateType.Email) && isDefined(accessCode)) {
            try {
                await getTopic(CreatorInfoComponent.amplitude_callsite, topicData.topicId, accessCode, false);
                this.isInvalidPassword = false;
            } catch (error) {
                this.isInvalidPassword = true;
                getMainPage().errorToastElement.showToastAndThrow('Unable to verify access. Please try again.');
            }
        }

        const topicid = topicData.topicId;
        const mapComponent = topicData.mapComponent;
        this.isReloading = true;

        let retries = 0;
        const num_retries = type === GateType.Password || type === GateType.Paywall ? 5 : 1;
        while (retries < num_retries) {
            await topicData.instantiateTopicData(
                CreatorInfoComponent.amplitude_callsite,
                accountInfo.isAuthenticated,
                false,
                false,
                topicid,
                accessCode,
                undefined,
                mapComponent
            );

            if (topicData.topic?.is_gate_limited === false) {
                // save password to local storage with topic_id_password
                if (type === GateType.Password || type === GateType.Email) {
                    localStorage.setItem(`${topicData.topicId}_password`, accessCode ?? '');
                    if (pointCache.points.length > 1000 ) {
                        getMainPage().errorToastElement.showToast('Access Granted! Because this map has many points, you may need to refresh the page to see them all');
                    }
                }
                await pointCache.instantiatePointCache(topicData.topic, topicData.topicKey, true);
                if (email !== undefined) {
                    await addBookmark(CreatorInfoComponent.amplitude_callsite, topicData.topicId, topicData.topicKey);
                };
                try {
                    if (type === GateType.Password) {
                        await this.addGatedAccessEvent(email, TopicGateType.Password, undefined, password);
                    } else if (type === GateType.Paywall) {
                        // we send this on the backend b/c it is more sentitive w/ payment info
                    } else if (type === GateType.Email) {
                        await this.addGatedAccessEvent(email, TopicGateType.Email);
                    }
                } catch (error) {
                    console.error('Unable to add gated access event');
                };
                this.isReloading = false;
                void show_confetti();
                this.hasUserGainedAccess = true;
                // add bookmark if email is defined
                if (email !== undefined) {
                    try {
                        await addBookmark(CreatorInfoComponent.amplitude_callsite, topicData.topicId, topicData.topicKey);
                    } catch (error) {
                        console.error('Failed to add bookmark:', error);
                    }
                }
                break;
            } else {
                retries++;
                if (retries === num_retries) {
                    this.isReloading = false;
                    this.errorOnSubmit = true;
                    getMainPage().errorToastElement.showToastAndThrow('Unable to verify access. Please try again or refresh the page.');
                }
                await new Promise((resolve) => setTimeout(resolve, 200));
            }
        }
    }

    private async processCheckoutSuccess(hostedPageId: string | null) {
        // process success on back end
        // try confetti but don't stop the flow if it fails
        try {
            void show_confetti();
        } catch (error) {
            console.error('Failed to start confetti:', error);
        }
        // give it some time to go through chargebee
        await delay(1500);
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.refreshAccess(this.email, GateType.Paywall, this.paywallGate?._id.$oid);
        this.isCheckingOut = false;
        this.processingGate = undefined;
    }

    private async addGatedAccessEvent(email: string | undefined, type: TopicGateType, gate_id?: string, password?: string, transaction_id?: string, transaction_amount?: number) {
        const ipAddress = await getIpAddress();
        if (type === TopicGateType.Password) {
            const gatedAccessEvent: CreateGatedAccessEventRequest = {
                email: email ?? undefined,
                ip_address: ipAddress ?? '',
                gate_type: type,
                password: password ?? '',
                name: accountInfo.fullName ?? '',
                transaction_id: undefined,
            };
            await addGatedAccessEvent(CreatorInfoComponent.amplitude_callsite, topicData.topicId, gatedAccessEvent);
        } else if (type === TopicGateType.Paywall) {
            console.error('Paywall gated access event is handled on the backend.');
        } else if (type === TopicGateType.Email) {
            const email_id_object: ObjectId = {$oid: this.emailGate?._id.$oid ?? ''};
            const gatedAccessEvent: CreateGatedAccessEventRequest = {
                email: email ?? undefined,
                ip_address: ipAddress ?? '',
                gate_type: type,
                email_gate_id: email_id_object,
                name: accountInfo.fullName ?? '',
                transaction_id: undefined,
            };
            await addGatedAccessEvent(CreatorInfoComponent.amplitude_callsite, topicData.topicId, gatedAccessEvent);
            let user_ip: string | undefined;

            try {
                const { ip } = await fetch('https://api64.ipify.org?format=json').then(async (response) => await response.json());
                user_ip = ip;
            } catch (error) {
                console.error('Failed to fetch IP address:', error);
            }

            const emailCaptureRequest = {
                email: email ?? '',
                ip_address: user_ip ?? '',
                text: {
                    title: `Gain Access to ${topicData.topic?.name ?? 'map'}`,
                    description: `Unlock access to ${topicData.topic?.name ?? 'map'} by entering your email.`,
                    cta: `Unlock Access ${this.emailGate?.opt_in_language ? `and ${this.emailGate.opt_in_language}` : ''}`
                }
            };

            await addEmailCapture(CreatorInfoComponent.amplitude_callsite, topicData.topicId, emailCaptureRequest);

        }
    }

    private async resendCode() {
        void login('Chargebee Paywall Payment', this.email, LoginOrigin.PasswordPaywall, topicData.topic?.name, topicData.topic?.header_logo);
    }

    private async confirmEmailAndUpgrade (email: string, code: string) {
        try  {
            await this.confirmEmail(email, code);
            this.invalidCode = false;
        } catch (e) {
            this.invalidCode = true;
            getMainPage().errorToastElement.showToastAndThrow('Unable to Verify Email. Please try again.');
        }
        await topicData.refreshTopicViewerEntitlements();
    }

    private async refreshAccessAfterPaymentOrPaymentConfirmation(email: string, code: string) {
        const emailAccess = undefinedIfEmpty(accountInfo.email) ?? email;
        await this.refreshAccess(emailAccess, GateType.Paywall, this.paywallGate?._id.$oid);

        if (this.userMaybeAlreadyPaid && !this.hasUserGainedAccess) {
            getMainPage().errorToastElement.showToast('If you are not able to see the content now, we did not find a payment for this map associated with your email. Please try again or contact support (help@proxi.co)');
        }
    }

    private async refreshAccessAfterEmailGate(email: string, code: string) {
        try {
            await this.refreshAccess(email, GateType.Email);
            this.showConfirmEmail = false;
            this.processingGate = undefined;
            this.hasUserGainedAccess = true;
        } catch (e) {
            getMainPage().errorToastElement.showToastAndThrow('Unable to Verify Email. Please try again.');

        }
    }

    private async confirmEmail(email: string, code: string, bookmark?: boolean) {
        try {
            await upgradeToken(CreatorInfoComponent.amplitude_callsite, email, code, undefined, LoginOrigin.PasswordPaywall);
            await accountInfo.instantiateAccountData(false);
            this.showConfirmEmailBookmark = false;
            await addBookmark(CreatorInfoComponent.amplitude_callsite, topicData.topicId, topicData.topicKey);
        } catch (e) {
           getMainPage().errorToastElement.showToastAndThrow('Unable to Verify Email. Please try again.');
        }
    }

    private newFocusPoint(e : CustomEvent) {
        this.dispatchEvent(new CustomEvent('on-point-focus', {
            detail: {
                point: e.detail.focusedPoint,
                focused: e.detail.focused
            }
        }));
        if (this.showAboutMap && e.detail.focused === true) {
            this.showAboutMap = false;
        }
    }

    triggerEvent(eventName: string, e: CustomEvent) {
        this.dispatchEvent(
            new CustomEvent(eventName, {
                detail: e.detail
            })
        );

        if (eventName === 'on-search-changed') {
            this.rerenderOutOfBounds();
        }
    }

    private rerenderOutOfBounds() {
        // wait for calculation & force a re-render or else it takes to long to update. Have to keep this separate from other calls because search takes a while to update
        // though theres probably a better way to do this - melinda
        setTimeout(() => {
            // this.pointsOutOfView = pointCache.points.length - pointCache.markersInBounds.length;
            this.onPointsChange();
        }, 1000);
    };

    private openPrintPage() {
        // add ?view=print&isPublicPrint=true to existing url then reload
        const currentUrl = window.location.href;
        const printableLink = currentUrl.includes('?') ? `${currentUrl}&view=print&isPublicPrint=true` : `${currentUrl}?view=print&isPublicPrint=true`;
        // create a link element and set the href to the printable link
        const link = document.createElement('a');
        link.href = printableLink;
        link.target = '_blank';
        link.click();
    }

    private backToList() {
        topicData.mobileDrawerFocusedPoint = undefined;
    }

    private get mobilePointView(): GetPointResponse | undefined {
        return topicData.mobileDrawerFocusedPoint;
    }

    private getChallengeLocation(point: GetPointResponse) {
        return challengeData.challengeLocations.find(
            (l) => isDefined(l.point_info) && l.point_info?._id.$oid === point._id.$oid && l.challenge_location_info?.is_active === true && l.gave_up !== true
        );
    }

    private isTreasureHuntClue(point: GetPointResponse): boolean {
        if (!isDefined(topicData.challengeHappeningNow) || !isDefined(challengeData.activatedChallengeId) || !challengeData.isType(ChallengeType.Treasure)) {
            return false;
        }
        const location = this.getChallengeLocation(point);
        return isDefined(location) && location.checked_in !== true;
    }

    render() {
        return html`
            <div id="creator-info-wrapper" class="creator-info-wrapper">
                ${(isDefined(this.profile_url) || topicData.topic?.hide_map_stats_in_info_drawer !== true || (topicData.topic?.description?.length ?? 0) > 0) ? html`
                    <div class="creator-info ${this.showAboutMap ? '' : 'creator-area-hidden'}">
                        <div class="creator-info__about ${this.showAboutMap ? '' : 'hidden'}">
                            ${isDefined(this.creator_profile_id) ? html`
                                <div class="creator-info__profile ">
                                    <creator-profile-card
                                        .creatorProfile=${this.profile}
                                        .imageSize=${CreatorProfileImageSize.SMALL}
                                        .showDescription=${!this.isMobile}
                                        .type=${CreatorProfileCardType.MAPINFODRAWER}>
                                    </creator-profile-card>

                                    ${isDefined(this.profile_url) && !this.isMobile ? html`
                                        <div class="other-maps">
                                            <div class="other-maps-button">
                                                <a class="unset_default_link see_more_maps" href=${this.profile_url} target="_blank">More Maps By @${this.profile?.username}</a>
                                                <mwc-icon class="other-maps-button-icon">arrow_forward</mwc-icon>
                                            </div>
                                        </div>`
                                    : ''}
                                </div>
                                <div class="divider"></div>`
                                : ''
                            }
                            <div class="creator-info__map">
                                <div class="map__header">
                                    ${topicData.topic?.hide_map_stats_in_info_drawer !== true ? html`
                                        <div class="map-details-wrapper">
                                            <div class="map-detail">
                                                <mwc-icon class="map-detail-icon">edit</mwc-icon>
                                                <translated-span class="location-detail-text">
                                                   Updated
                                                </translated-span>
                                                <translated-span class="location-detail-text">
                                                    ${isDefined(topicData?.topic) ? `${format(new Date(topicData?.topic?.updated_at), 'MMM dd, yyyy')}` : ''}
                                                </translated-span>
                                            </div>

                                            <div class="map-detail">
                                                <mwc-icon class="map-detail-icon">visibility</mwc-icon>
                                                <span class="location-detail-text">${topicData?.topic?.statistics.approximate_topic_views ?? 0} <translated-span>Views</translated-span></span>
                                            </div>

                                            <div class="map-detail">
                                                <mwc-icon class="map-detail-icon">place</mwc-icon>
                                                <span class="location-detail-text">${pointCache.points.length ?? 0} <translated-span>Locations</translated-span></span>
                                            </div>
                                        </div>
                                    ` : ''}
                                </div>

                                <p class="map__desc for-translation">
                                    <translated-span>${topicData?.topic?.description ?? ''}</translated-span>
                                </p>
                            </div>
                        </div>
                        <div class="expand-div">
                            <p class="map__desc expand" @click="${this.aboutMapClick}">
                                ${this.showAboutMap ? html`
                                    <mwc-icon class="about-icon">keyboard_double_arrow_up</mwc-icon> <translated-span class="for-translation">Hide Summary</translated-span>`
                                : html`
                                    <mwc-icon class="about-icon">keyboard_double_arrow_down</mwc-icon> <translated-span class="for-translation">Show Summary</translated-span>`
                                }
                            </p>
                        </div>
                    </div>
                ` : ''}
                ${topicData.topic?.disable_command_center_buttons !== true && !this.isAdminPortal ? html`
                    <div class="drawer-buttons-group">
                        ${this.isMobile || topicData.topic?.hide_send_map_to_phone === true ? '' : html`
                            <form-button
                                class="drawer-inner-btn"
                                variant="outline"
                                size="sm"
                                ?fullWidth=${true}
                                @click="${async () => await topicData.mapComponent?.setMapDrawer(MapDrawer.SendToPhone)}">
                                <mwc-icon class="drawer-inner-btn-icon">mark_chat_unread</mwc-icon>
                                <translated-span> Send This Map To My Phone </translated-span>
                            </form-button>
                        `}
                        <div class="flexContainer">
                            ${topicData.topic?.hide_bookmark_buttons !== true ? html`
                                <bookmark-map-button
                                    id="bookmark-drawer-btn"
                                    variant="horizontal"
                                    .mobilePreview=${this.mobilePreview}
                                    .topic=${topicData.topic}
                                >
                                </bookmark-map-button>
                            ` : ''}
                            ${topicData.topic?.hide_print_button !== true && featuresInfo.printableMapEnabled && !detectMobileViewport() ? html`
                                <form-button
                                    class="drawer-inner-btn"
                                    variant="outline"
                                    size="sm"
                                    ?fullWidth=${true}
                                    @click="${() => this.openPrintPage()}">
                                    <mwc-icon class="drawer-inner-btn-icon">print</mwc-icon>
                                    <translated-span>Print Map</translated-span>
                                </form-button>
                            ` : ''}
                            ${topicData.topic?.disable_share_button !== true ? html`
                                <form-button
                                    class="drawer-inner-btn"
                                    ?vertical=${this.isMobile}
                                    size="sm"
                                    ?fullWidth=${true}
                                    @click="${async () => await topicData.mapComponent?.setMapDrawer(MapDrawer.ShareMap)}">
                                    <mwc-icon class="drawer-inner-btn-icon">ios_share</mwc-icon> <translated-span>Share</translated-span>
                                </form-button>
                            ` : ''}
                            ${this.isMobile && topicData.topic?.hide_send_map_to_phone !== true ? html`
                                <form-button
                                    class="drawer-inner-btn"
                                    variant="outline"
                                    size="sm"
                                    ?vertical=${this.isMobile}
                                    ?fullWidth=${true}
                                    @click="${async () => await topicData.mapComponent?.setMapDrawer(MapDrawer.SendToPhoneHomescreen)}">
                                    <mwc-icon class="drawer-inner-btn-icon">phone_iphone</mwc-icon>
                                    <translated-span> Save to Home Screen </translated-span>
                                </form-button>
                        ` : ''}
                        </div>
                    </div>
                ` : ''}
                ${topicData.topic?.is_gate_limited !== true && !this.isUserPreview || (this.isUserPreview && !(this.hasGate)) ? html`
                    <div class="mid-container">
                        ${topicData.topic?.disable_search_button !== true ? html`
                            <div class="search-filter">
                                <mwc-textfield
                                    outlined
                                    id="search"
                                    icon="search"
                                    class="input searchinput"
                                    placeholder="Search"
                                    @input="${() => {
                                        const detail: { value: string } = { value: this.search.value };
                                        this.triggerEvent('on-search-changed', new CustomEvent('event-name', { detail }));
                                    }}"
                                >
                                </mwc-textfield>
                                <paper-tooltip for="search" offset="0">Use this search field to filter by category, city, state, etc.</paper-tooltip>
                            </div>
                        ` : ''}
                        ${topicData.topic?.disable_list_button !== true ? html`
                            ${this.mobilePointView ? html`
                                <div class="mobile-point-detail">
                                    <form-button
                                        variant="outline"
                                        size="sm"
                                        @click=${() => this.backToList()}
                                    ><mwc-icon aria-hidden="true" style="--mdc-icon-size:16px; margin-right:4px">arrow_back</mwc-icon> Back to list</form-button>
                                    ${this.isTreasureHuntClue(this.mobilePointView) ? html`
                                        <clue-point-card
                                            style="margin-top: 8px; width: 100%;"
                                            .challenge_points=${this.getChallengeLocation(this.mobilePointView)?.challenge_location_info?.value}
                                            .clueDetails=${this.getChallengeLocation(this.mobilePointView)?.challenge_location_info?.clue_details}
                                            .geo_location_eligible=${false}
                                        ></clue-point-card>
                                    ` : html`
                                        <expanded-point-card
                                            .point=${this.mobilePointView}
                                            .listOfPointsView=${true}
                                            style="margin-top: 8px;"
                                        ></expanded-point-card>
                                    `}
                                </div>
                            ` : html`
                            ${this.searchEverywhere ? html`
                                <span @click=${() => {this.searchEverywhere = false;}} class="limit-to-viewport">
                                    Only Show Results in Map View
                                </span>
                            ` : ''}
                            <point-list
                                id="pointList"
                                style="display: block; overflow-y:auto;flex:0 1 auto"
                                .location=${'creator-info-component'}
                                .maxLoad=${this.isMobile ? 20 : 50}
                                .mobilePreview=${this.mobilePreview}
                                .searchEverywhereClicked="${this.searchEverywhere}"
                            >
                            </point-list>
                            `}
                            ${this.search?.value?.length > 1 && !this.searchEverywhere && this.pointsOutOfView > 0 ? html`
                                <span class="view-more-button" style="background-color:${topicData.topic?.header_color};color:${topicData.topic?.header_text_color}" @click=${async () => {this.searchEverywhere = true;}}>
                                    ${this.pointsOutOfView} ${topicData.topic?.point_name ?? 'Point'}s Outside of View
                                </span>
                            ` : ''}
                        ` : ''}
                        </div>`
                    : html`
                        <div class="mid-container empty">
                        </div>
                `}
                ${!this.showGatedAccessSection ? '' : html`
                    ${this.renderGatedAccess()}
                `}
            </div>
            <error-toast id="errorToast"></error-toast>
        `;
    }

    get showGatedAccessSection() {
        return (this.hasGate && !this.isAdminPortal && !this.closeGatedAccess) || this.isUserPreview;
    }

    get collectEmailHeading() {
        switch (this.processingGate) {
            case 'paywall':
                return this.userMaybeAlreadyPaid ? 'Verify Your Email' : 'Enter Your Email Address';
            case 'email':
                return 'Enter Your Email Address';
            case 'password':
                return 'Enter Your Email to "Bookmark" This Map';
            default:
                return 'Enter Your Email Address';
        }
    }

    get confirmEmailActionText() {
        switch (this.processingGate) {
            case 'paywall':
                return this.userMaybeAlreadyPaid ? 'Verify' : 'Check Out';
            case 'email':
                return 'Get Access';
            case 'password':
                return 'Bookmark';
            default:
                return 'Get Access';
        }
    }

    get inAccessProcess() {
        return this.showGetEmail || this.showConfirmEmail || this.showConfirmEmailBookmark || this.isCheckingOut || this.processingGate === 'email' || this.processingGate === 'password';
    }

    private async onSubmitEmailClick() {
        try {
            const email = this.emailInputElement.value.toLowerCase().trim();
            if (!validateEmail(email)) {
                console.error('Invalid email address:', email);
                this.invalidEmail = true;
                getMainPage().errorToastElement.showToastAndThrow('Please enter a valid email address');
                return;
            }
            this.invalidEmail = false;
            this.email = email;
            switch (this.processingGate) {
                case 'paywall':
                    if (this.userMaybeAlreadyPaid) {
                        await this.submitEmailForPurchaseVerification(this.email);
                        break;
                    } else {
                        await this.submitEmailForPaywall(this.email);
                        break;
                    }
                case 'email':
                    await this.submitEmailForEmailGate(this.email);
                    break;
                case 'password':
                    await this.submitEmailForBookmark(this.email);
                    break;
                default:
            }
            this.showGetEmail = false;
        } catch (error) {
            getMainPage().errorToastElement.showToastAndThrow('An error occurred while processing your request. Please try again.');
        }
    }

    get visibleProductIds(): string[] {
        if (this.paywallGate === undefined) {
            return [];
        }
        const visibleProductIds: string[] = [];
        if ('Paywall' in this.paywallGate.item_details) {
            visibleProductIds.push(this.paywallGate._id.$oid);
        }
        return visibleProductIds;
    }

    get paywall_gate_id(): ObjectId | undefined {
        return this.paywallGate?.item_details && 'Paywall' in this.paywallGate?.item_details ? this.paywallGate?.item_details.Paywall.gate_id : undefined;
    }

    private async onVerifyEmailClick() {
        switch (this.processingGate) {
            case 'paywall':
                if (this.userMaybeAlreadyPaid) {
                    getMainPage().errorToastElement.showToast('Verifying email and refreshing access... please wait.');
                    await this.confirmEmailAndUpgrade(this.email, this.codeInputElement.value);
                    await this.refreshAccessAfterPaymentOrPaymentConfirmation(this.email, this.codeInputElement.value);
                } else {
                    getMainPage().errorToastElement.showToast('Verifying email and redirecting to checkout... please wait.');
                    await this.confirmEmailAndUpgrade(this.email, this.codeInputElement.value);
                    if (this.preCheckoutElement && topicData.topic && accountInfo.isAuthenticated && isDefined(this.paywall_gate_id)) {
                        const request: CreatePaywallRoleAssignmentRequest = {topic_id: topicData.topic?._id, gate_id: this.paywall_gate_id, email: this.email ?? ''};
                        const response = await startStripeCheckoutSession(this.email, this.preCheckoutElement?.remainingLineItems, topicData.organizationId ?? '', topicData.topicId, PaymentTargetType.Paywall, request, undefined, topicData.topicKey, undefined);
                        this.isCheckingOut = true;
                        this.processingGate = 'paywall';
                        this.showConfirmEmail = false;
                        // open url in same tab
                        if (isDefined(response) && isDefined(response.url)) {
                            window.location.href = response.url;
                        }
                    } else {
                        this.isCheckingOut = false;
                        this.processingGate = undefined;
                        getMainPage().errorToastElement.showToastAndThrow('Unable to proceed to checkout at this time. Please try again later.');
                    }
                }
                break;
            case 'email':
                getMainPage().errorToastElement.showToast('Verifying email and upgrading access... please wait.');
                await this.confirmEmailAndUpgrade(this.email, this.codeInputElement.value);
                await this.refreshAccessAfterEmailGate(this.email, this.codeInputElement.value);
                break;
            case 'password':
                getMainPage().errorToastElement.showToast('Verifying email and saving map... please wait.');
                await this.confirmEmailAndUpgrade(this.email, this.codeInputElement.value);
                break;
            default:
        }
        this.showConfirmEmail = false;
        this.showConfirmEmailBookmark = false;
        this.processingGate = undefined;
    }

    renderGatedAccess() {
        return html`
            <div class="gated-access-all">
                ${topicData.topic?.is_gate_limited === true || (this.isUserPreview && (isDefined(this.paywallGate) || isDefined(this.hasEmailGate) || isDefined(this.hasPasswordGate))) ? html`
                    <h2 class="get_access">Access The Full Guide</h2>
                    <div class="gated-access-wrapper ${this.showGetEmail || this.showConfirmEmail || this.isCheckingOut ? 'focus-input' : ''}">
                        ${this.isReloading && !this.isCheckingOut ? html`
                            <loading-element
                                loadingString="Verifying Access"
                                style="margin-bottom: 10px"
                            >
                            </loading-element>
                        ` : ''}
                        ${this.paywallGate ? html`
                            <pre-checkout-component
                                id="preCheckout"
                                style="margin-bottom:10px"
                                .product_ids=${this.visibleProductIds}
                                .organization_id=${topicData.topic?.organization_id?.$oid ?? ''}
                                .payment_target_type=${PaymentTargetType.Paywall}
                            >
                            </pre-checkout-component>` : ''
                        }
                        ${this.showGetEmail ? html`
                            <h4>${this.collectEmailHeading}</h4>
                            ${this.invalidEmail ? html`
                                <span style="font-size:0.8rem;margin-bottom:5px;margin-top:5px;color:red">Please enter a valid email address</span>`
                            : ''}
                            ${this.hasEmailGate ? html`
                                <div style="width: 100%">
                                    ${undefinedIfEmpty(this.emailGate?.opt_in_language) ? html`
                                        <div class="checkbox-wrapper">
                                            <form-checkbox
                                                id="emailGateOptIn"
                                                name="emailGateOptIn"
                                                ?checked="${true}"
                                                >
                                            </form-checkbox>
                                            <span>${this.emailGate?.opt_in_language}</span>
                                        </div>`
                                    : ''}
                                </div>
                            ` : ''}
                            <form-input-button
                                id="emailInput"
                                readonly="false"
                                placeholder="Email Address"
                                value="${this.email ?? ''}"
                                @submit="${this.onSubmitEmailClick}"
                            >
                               Continue
                            </form-input-button>
                        ` : ''}

                        ${this.showConfirmEmail ? html`
                            <h4>${this.processingGate === 'paywall' ? 'Confirm Your Email' : 'Confirm Your Email'}</h4>
                            <span style="font-size:0.8rem;margin-bottom:10px"> We just sent an email to ${this.email} with a six digit confirmation code. Enter that code here ${this.userMaybeAlreadyPaid ? 'so we can confirm your purchase' : ''}.</span>
                            ${this.invalidCode ? html`
                                <span style="font-size:0.8rem;margin-bottom:5px;margin-top:5px;color:red">Invalid Confirmation Code. Please try again.</span>`
                            : ''}
                            <div style="width: 100%">
                                <form-input-button
                                    id="codeInput"
                                    readonly="false"
                                    placeholder="Confirmation Code"
                                    @submit="${this.onVerifyEmailClick}"
                                >${this.confirmEmailActionText}
                                </form-input-button>
                                <span style="font-size:0.8rem;margin-top:5px;text-decoration:underline" @click=${this.resendCode}> Resend Code</span>
                            </div>
                        ` : ''}

                        ${this.isCheckingOut && this.processingGate === 'paywall' ? html`
                            <h4>Complete Your Purchase</h4>
                            <span style="font-size:0.8rem;margin-bottom:10px"> You are being redirected to our payment provider to complete your purchase.
                                <br><b>${this.isMobile ? 'If you have successfully completed your payment, and are seeing this message, you can try refreshing the page for access.' : ''}</b>
                            <loading-element
                                loadingString="Listening for Success"
                                style="margin-bottom: 10px"
                            >
                            </loading-element>
                        ` : ''}

                        ${isDefined(this.paywallGate) && !this.inAccessProcess ? html`
                            <div style="width: 100%; display: flex; flex-direction: column; align-items: stretch;">
                                <form-button
                                    variant="outline"
                                    class="action-button"
                                    @click="${async () => {
                                        this.processingGate = 'paywall';
                                        if (accountInfo.isAuthenticated) {
                                            if (this.preCheckoutElement && this.paywallGate && topicData.topic) {
                                                this.isCheckingOut = true;
                                                const request: CreatePaywallRoleAssignmentRequest = {topic_id: topicData.topic?._id, gate_id: this.paywallGate?._id ?? '', email: this.email ?? accountInfo.email ?? ''};
                                                const response = await startStripeCheckoutSession(this.email ?? accountInfo.email ?? '', this.preCheckoutElement?.remainingLineItems, topicData.organizationId ?? '', topicData.topicId, PaymentTargetType.Paywall, request, undefined, topicData.topicKey, undefined);
                                                // open url in same tab
                                                if (isDefined(response) && isDefined(response.url)) {
                                                    window.location.href = response.url;
                                                }
                                            }
                                        } else {
                                            this.showEmail();
                                        }
                                    }}"
                                    icon="payment"
                                    ?fullWidth="${true}"
                                >
                                    Pay Now: ${this.preCheckoutElement ? this.preCheckoutElement.totalCostString : ''}
                                </form-button>
                                <div style="font-size:0.8rem;margin-top:5px;margin-bottom:5px;cursor:pointer;text-decoration:underline" @click="${this.alreadyPaidClick}">Already Purchased? Confirm Your Email Here.</div>
                            </div>
                        ` : ''}

                        ${this.hasEmailGate && !this.inAccessProcess ? html`
                            <div style="width: 100%">
                                <form-button
                                    variant="outline"
                                    class="action-button"
                                    @click="${async () => {
                                        this.processingGate = 'email';
                                        this.showEmail();
                                    }}"
                                    icon="email"
                                    ?fullWidth="${true}"
                                >
                                        Enter Email to Get Access
                                </form-button>
                            </div>

                        ` : ''}

                        ${this.hasPasswordGate && !this.inAccessProcess ? html`
                            <div style="width: 100%">
                                    <form-button
                                        variant="outline"
                                        class="action-button"
                                        @click="${async () => {
                                            this.processingGate = 'password';
                                        }}"
                                        ?fullWidth="${true}"
                                        icon="password"
                                    >
                                        <span>Enter A Password</span>
                                    </form-button>
                            </div>
                        ` : ''}

                        ${this.processingGate === 'password' ? html`
                            <div style="width: 100%">
                                ${this.isInvalidPassword ? html`
                                    <span style="font-size:0.8rem;margin-bottom:5px;margin-top:5px;color:red">Cannot Validate Password</span>`
                                : ''}
                                <form-input-button
                                    id="passwordInput"
                                    readonly="false"
                                    placeholder="Enter Password"
                                    .valid="${!this.isInvalidPassword}"
                                    @submit="${this.sendPassword}"
                                >
                                    Get Access
                                </form-input-button>
                            </div>
                        ` : ''}
                    ` :
                    html`
                        ${this.hasUserGainedAccess && topicData.topic?.is_gate_limited === false && !this.isUserPreview && !this.closeGatedAccess ? html`
                            <div class="gated-access-wrapper accessed-note">
                                <h2 class="get_access">You're In!</h2>
                                ${this.isMobile ? html`
                                    <mwc-icon class="drawer-inner-btn-icon" style="position:absolute;bottom:150;right:0" @click=${this.closeGatedAccessClick}>close</mwc-icon>
                                ` : ''}
                                <span style="font-size:0.8rem;margin-bottom:10px">
                                    You've got full access to this guide.
                                </span>
                                ${(accountInfo.email === '' || !accountInfo.email) && !this.showConfirmEmailBookmark ? html`
                                    <translated-span style="font-size:0.8rem;margin-bottom:10px">
                                        Provide your email to bookmark this guide so you can access it again later.
                                    </translated-span>
                                    <form-input-button
                                        id="emailInputBookmark"
                                        readonly="false"
                                        placeholder="Enter Email Address"
                                        value="${this.email ?? ''}"
                                        @submit="${async () => await this.submitEmailForBookmark(this.emailInputBookmarkElement.value)}"
                                    >
                                        Save
                                    </form-input-button>
                                ` : ''}
                                ${this.showConfirmEmailBookmark ? html`
                                    <translated-span style="font-size:0.8rem;margin-bottom:10px">
                                        We sent you an email to verify your email address. Please enter the code below.
                                    </translated-span>
                                    <div style="width: 100%">
                                        <form-input-button
                                            id="codeInputBookmark"
                                            readonly="false"
                                            placeholder="Confirmation Code"
                                            @submit="${async () => await this.confirmEmail(this.email, this.codeInputBookmarkElement.value, true)}"
                                        >Submit
                                        </form-input-button>
                                        <span style="font-size:0.8rem;margin-top:5px;text-decoration:underline" @click=${this.resendCode}> Resend Code</span>
                                    </div>
                                `: ''}
                            </div>
                        ` : ''}
                    `}
                </div>
        `;
    }

    static get styles() {
        return css`
            :host {
                font-family: var(--custom-font), 'Montserrat', sans-serif !important;
                display: flex;
                min-width: 200px;
                flex: 1;
            }

            h4 {
                margin: 5px;
            }

            .flexContainer {
                display: flex;
                gap: 0.5rem;
            }

            #close {
                position: absolute;
                top: 1rem;
                right: 1rem;
                cursor: pointer;
            }

            .creator-info-wrapper {
                position: relative;
                width: 100%;
                height: 100%;
                box-sizing: border-box;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: flex-start;
                font-size: 0.66667rem;
                font-style: normal;
                font-weight: 400;
                line-height: 150%;
                gap: 0.5rem;
                padding-bottom: 10px;
            }

            .gated-access-wrapper {
                width: 100%;
                box-sizing: border-box;
                display: flex;
                flex-direction: column;
                align-items: stretch;
                justify-content: flex-start;
                font-size: 0.8rem;
                text-align: center;
                gap: 10px;
            }

            .focus-input {
                background-color: var(--main-featured-color);
                border-radius: 8px;
                padding: 1rem;
            }

            .accessed-note {
                border-color: var(--main-success-color);
                border-style: solid;
                border-width: 1px;
                border-radius: 8px;
                padding: 0.5rem;
            }

            .get_access {
                font-size: 1rem;
            }

            .about-icon {
                --mdc-icon-size: 16px;
                margin-right: 4px;
            }

            .purchase-btn {
                --btn-background-color: var(--main-success-color);
                --btn-label-color: white;
                width: 100%;
                font-size: 0.75rem;
            }

            mwc-textfield {
                height: 30px;
                --mdc-typography-subtitle1-font-size: 12px;
                --mdc-icon-size: 1rem;
            }

            .hidden {
                display: none;
            }

            .creator-info__header {
                position: relative;
                width: 100%;
                overflow: hidden;
            }

            .creator-info__header::before {
                border-radius: 100%;
                position: absolute;
                inset: 100px -10% 0px;
                content: "";
                height: 250px;
                width: 120%;
                background: #F6F7F7;
            }

            .see_more_maps {
                text-transform: capitalize;
                font-weight: 600;
                font-size: 0.75rem;
            }

            .gated-access-all {
                box-sizing: border-box;
                width: 100%;
                display: flex;
                flex-direction: column;
                align-items: center;
                padding: 0;
            }

            .bg-mask {
                position: absolute;
                bottom: 0;
                left: 0;
                width: 100%;
            }

            .avatar {
                width: 136px;
                height: 136px;
                border-radius: 50%;
                background: lightgray 50% / cover no-repeat;
                display: flex;
                align-items: center;
                justify-content: center;
            }

            .avatar > img {
                width: 136px;
                height: 136px;
                border-radius: 50%;
                position: absolute;
            }

            .unset_default_link {
                text-decoration: none;
                color: inherit;
            }

            .profile__header {
                width: 100%;
            }

            form-input-button {
                width: 100%;
            }

            .creator-name {
                color: var(--main-primary-color);
                margin: 0;
            }

            .creator-title {
                color: var(--main-primary-color);
                margin: 0;
            }

            .creator-desc {
                color: var(--theme-input-color);
                margin: 0;
            }

            .other-maps {
                width: 100%;
                text-align: left;
                margin-bottom: 0.5rem;
            }

            .limit-to-viewport {
                justify-content: center;
                width: 100%;
                display: flex;
                align-items: center;
                text-decoration: underline;
                margin-bottom: 5px;
                cursor: pointer;
            }

            .other-maps-button {
                width: fit-content;
                display: flex;
                align-items: center;
                justify-content: flex-start;
                gap: 4px;
                color: var(--main-invalid-color);
                cursor: pointer;
            }

            .checkbox-wrapper {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: center;
                margin-bottom: 5px;
                gap: 8px;
                line-height: 120%;
                color: #102C3D;
                margin-top: 15px;
            }

            .other-maps-button-icon {
                color: var(--main-invalid-color);
                --mdc-icon-size: 16px;
            }

            .divider {
                width: 100%;
                height: 1px;
                background: var(--theme-border-color);
                margin: 0.5rem 0;
                z-index: 1;
                box-sizing: border-box;
            }

            .creator-info__map {
                width: 100%;
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                gap: 0.5rem;
                z-index: 1;
            }


            .creator-info {
                width: 100%;
                box-sizing: border-box;
                overflow: hidden;
                background-color: #F7F8F9;
                padding: 0 0.5rem;
                display: flex;
                flex-direction: column;
                padding-top: 0.5rem;
            }

            .creator-info__about {
                flex: 1;
                flex-basis: 20%;
                overflow-y: scroll;
            }

            .creator-info__about::-webkit-scrollbar {
                width: 5px;
            }

            .creator-info__about::-webkit-scrollbar-track {
                background: var(--theme-background-color);
            }

            .creator-info__about::-webkit-scrollbar-thumb {
                background: var(--theme-border-color);
            }

            .creator-area-hidden {
                padding: 0 1.5rem
            }

            .map__header {
                width: 100%;
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                gap: 4px;
            }

            .map-title {
                color: #091E2C;
                margin: 0.75rem 0;
                font-size: 1rem;
            }

            .map-details-wrapper {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: space-between;
                flex-wrap: wrap;
                gap: 4px;
            }

            .map-detail {
                display: flex;
                align-items: center;
                justify-content: flex-start;
            }

            .map-detail-icon {
                --mdc-icon-size: 12px;
                margin-right: 2px;
            }

            .location-detail-text {
                color: var(--theme-input-color);
                line-height: 150%;
            }

            .map__desc {
                color: var(--theme-input-color);
                margin: 0;
                text-align: justify;
            }

            .view-more-button {
                padding: 5px;
                border-radius: 5px;
                cursor: pointer;
                display: flex;
                justify-content: center;
                margin: 5px 15px;
            }

            .expand {
                text-align: center;
                margin-top: 0.5rem;
                margin-bottom: 0.5rem;
                cursor: pointer;
                display: flex;
                font-weight: 600;
                text-align: center;
            }

            .expand-div {
                display: flex;
                flex-direction: row;
                align-items: center;
                justify-content: center;
            }

            .drawer-buttons-group {
                box-sizing: border-box;
                width: 100%;
                display: flex;
                flex-direction: column;
                gap: 0.5rem;
            }

            .drawer-inner-btn-icon {
                margin-right: 0.5rem;
                --mdc-icon-size: 1rem;
            }

            .drawer-mobile-btn-icon {
                margin-bottom: 0.5rem;
            }

            .drawer-inner-btn {
                flex: 1;
            }

            #bookmark-drawer-btn {
                flex: 1;
            }

            .mid-container {
                box-sizing: border-box;
                flex: 1 1 40%;
                width: 100%;
                overflow-y: auto;
                overflow-x: scroll;
                display: flex;
                flex-direction: column;
            }

            .mid-container.empty {
                flex: 1 1 0%;
            }

            .mid-container::-webkit-scrollbar {
                width: 5px;
            }

            .mid-container::-webkit-scrollbar-track {
                background: var(--theme-background-color);
            }

            .mid-container::-webkit-scrollbar-thumb {
                background: var(--theme-border-color);
            }

            .mid-mobile-container {
                flex: 1;
                overflow-y: auto;
                padding: 1rem 0 3.5rem 0;
            }

            .search-filter {
                margin-bottom: 1rem;
            }

            .searchinput {
                align-content: center;
                justify-content: center;
                overflow: hidden;
                inner-border: 1px solid var(--theme-border-color);
                width: 99%;
            }

            .search-filter #search {
                width: 100%;
                margin-top: 5px;
            }

            @media only screen and (max-width: 550px) {
                :host {
                    display: flex;
                    width: unset;
                }

                .creator-info-wrapper {
                    padding: 0;
                }

                #close {
                    display: none;
                }

                .expand {
                    margin-top: 0;
                }

                .expand-div {
                    background-color: var(--theme-lightest-color);
                }

                .creator-info {
                    margin: 0;
                    padding: 0.5rem;
                    border-radius: 8px;
                }
                .drawer-inner-btn-icon {
                    margin-right: 0;
                    margin-bottom: 2px;
                }

                .creator-info__header::before {
                    inset: 40% -10% 0px;
                    height: 200px;
                }

                .creator-info__profile {
                    gap: 10px;
                }

                .drawer-buttons-group {
                    padding: 0;
                    gap: 0.5rem;
                }

                .mid-container {
                    padding: 0;
                }

                .avatar {
                    width: 100px;
                    height: 100px;
                }

                .avatar > img {
                    width: 100px;
                    height: 100px;
                }

                .creator-name {
                    font-size: 20px;
                }

                .other-maps-button-icon {
                    --mdc-icon-size: 21px;
                }

                .creator-info__map {
                    gap: 10px;
                }

                .map-title {
                    font-size: 1.25rem;
                    line-height: 1.25rem;
                    text-align: center;
                }

                .gated-access-all {
                    padding: 0;
                }

                .map-details-wrapper {
                    justify-content: center;
                    gap: 10px;
                }

                .map-detail-icon {
                    --mdc-icon-size: 21px;
                    margin-right: 4px;
                }

            }
        `;
    }
}